import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { BadgeDollarSign, Coins, Gift, MousePointerClick, ShieldAlert } from 'lucide-react';
import { affiliatesAPI, reportsAPI } from '../../lib/api';
import { t } from '../../lib/i18n';
import { ProductBadge } from '../../components/products';
import { inputCls, panelCls } from '../../components/ui';

function Kpi({ label, value, accent = false, icon: Icon }) {
  return (
    <div className={`${panelCls} p-4`}>
      <div className="flex items-center gap-2">
        {Icon ? <Icon size={15} className="text-[var(--mms-sidebar)]" /> : null}
        <span className="text-[10.5px] font-semibold uppercase tracking-wide text-mms-faint">{label}</span>
      </div>
      <div className={`mt-2.5 text-2xl font-bold mms-num ${accent ? 'text-mms-accent' : 'text-mms-text'}`}>
        {typeof value === 'number' ? value.toLocaleString(undefined, { maximumFractionDigits: 2 }) : value}
      </div>
    </div>
  );
}

const ACTION_STYLES = {
  payouts: { icon: BadgeDollarSign, tint: 'rgba(247,184,75,0.14)', fg: '#b9821f' },
  commissions: { icon: Coins, tint: 'rgba(41,156,219,0.14)', fg: '#1c6f9e' },
  redemptions: { icon: Gift, tint: 'rgba(10,179,156,0.14)', fg: '#0a8f7e' },
  flags: { icon: ShieldAlert, tint: 'rgba(240,101,72,0.14)', fg: '#c23a1f' },
};

function ActionCard({ to, label, value, kind }) {
  const s = ACTION_STYLES[kind];
  const Icon = s.icon;
  return (
    <Link to={to} className={`${panelCls} p-4 hover:border-[var(--mms-hover-border)]`}>
      <div className="flex items-center justify-between">
        <span className="text-xs text-mms-muted">{label}</span>
        <span className="flex h-[26px] w-[26px] items-center justify-center rounded-md" style={{ background: s.tint, color: s.fg }}>
          <Icon size={14} />
        </span>
      </div>
      <div className="mt-2 text-2xl font-bold mms-num text-mms-text">{value}</div>
    </Link>
  );
}

export default function DashboardPage() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [byProduct, setByProduct] = useState(null);

  useEffect(() => {
    reportsAPI.points().then(({ data }) => setByProduct(data.products)).catch(() => setByProduct([]));
  }, []);

  useEffect(() => {
    affiliatesAPI.dashboard(days)
      .then(({ data }) => setData(data))
      .catch(() => toast.error(t('Could not load the dashboard')));
  }, [days]);

  if (data === null) return <p className="text-sm text-mms-muted">{t('Loading…')}</p>;
  const ai = data.action_items || {};
  const top = data.top_affiliates || [];
  const maxClicks = Math.max(1, ...top.map((a) => a.clicks || 0));

  return (
    <div>
      {/* Title + range */}
      <div className="mb-5 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-mms-text">{t('Dashboard')}</h1>
          <p className="mt-1 text-[12.5px] text-mms-muted">{t('Affiliate performance and the points economy at a glance.')}</p>
        </div>
        <select className={`${inputCls} !w-40`} value={days} onChange={(e) => setDays(Number(e.target.value))}>
          <option value={7}>{t('Last 7 days')}</option>
          <option value={30}>{t('Last 30 days')}</option>
          <option value={90}>{t('Last 90 days')}</option>
        </select>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <Kpi label={t('Clicks')} value={data.clicks} icon={MousePointerClick} />
        <Kpi label={t('Unique clicks')} value={data.unique_clicks} />
        <Kpi label={t('Signups')} value={data.signups} />
        <Kpi label={t('Paid conversions')} value={data.paid_conversions} />
        <Kpi label={t('Referred revenue')} value={data.revenue} accent />
      </div>

      {/* Points economy */}
      <h2 className="mb-3 mt-7 text-[13px] font-semibold uppercase tracking-wide text-mms-muted">{t('Points economy')}</h2>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Kpi label={t('Points outstanding (liability)')} value={data.points?.outstanding ?? 0} />
        <Kpi label={t('Points earned')} value={data.points?.earned ?? 0} />
        <Kpi label={t('Points redeemed')} value={data.points?.redeemed ?? 0} />
        <div className={`${panelCls} p-4`}>
          <span className="text-[10.5px] font-semibold uppercase tracking-wide text-mms-faint">{t('Redemption rate')}</span>
          <div className="mt-2.5 text-2xl font-bold mms-num text-mms-text">{Math.round((data.points?.redemption_rate ?? 0) * 100)}%</div>
          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-mms-raised">
            <div className="h-full rounded-full bg-mms-accent" style={{ width: `${Math.round((data.points?.redemption_rate ?? 0) * 100)}%` }} />
          </div>
        </div>
      </div>

      {/* Points by product (last 6 months) → Reports */}
      {byProduct && byProduct.length > 0 && (
        <>
          <div className="mb-3 mt-7 flex items-center justify-between">
            <h2 className="text-[13px] font-semibold uppercase tracking-wide text-mms-muted">{t('Points by product · last 6 months')}</h2>
            <Link to="/admin/reports" className="text-xs text-mms-accent hover:underline">{t('Open reports')}</Link>
          </div>
          <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6">
            {byProduct.map((p) => (
              <div key={p.product} className={`${panelCls} p-4`}>
                <ProductBadge product={p.product} />
                <div className="mt-2 text-xl font-bold mms-num text-mms-text">{Number(p.points || 0).toLocaleString()}</div>
                <div className="text-xs text-mms-faint mms-num">{p.members} {t('members')}</div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Action items + Top affiliates */}
      <div className="mt-7 grid grid-cols-1 gap-5 lg:grid-cols-2">
        <div>
          <h2 className="mb-3 text-[13px] font-semibold uppercase tracking-wide text-mms-muted">{t('Action items')}</h2>
          <div className="grid grid-cols-2 gap-4">
            <ActionCard to="/admin/payouts" kind="payouts" label={t('Pending payouts')} value={ai.pending_payouts ?? 0} />
            <ActionCard to="/admin/ledger" kind="commissions" label={t('Pending commissions')} value={ai.pending_commissions ?? 0} />
            <ActionCard to="/admin/catalog" kind="redemptions" label={t('Pending redemptions')} value={ai.pending_redemptions ?? 0} />
            <ActionCard to="/admin/fraud" kind="flags" label={t('Open fraud flags')} value={ai.open_flags ?? 0} />
          </div>
        </div>

        <div>
          <h2 className="mb-3 text-[13px] font-semibold uppercase tracking-wide text-mms-muted">{t('Top affiliates by clicks')}</h2>
          <div className={`${panelCls} p-2`}>
            {top.length === 0 ? (
              <p className="p-3 text-sm text-mms-faint">{t('No clicks in this period yet.')}</p>
            ) : (
              top.map((a) => {
                const name = a.member?.name || a.mid;
                const initials = String(name).trim().slice(0, 2).toUpperCase();
                return (
                  <div key={a.mid} className="flex items-center gap-3 px-3 py-2.5">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-mms-raised text-[10px] font-semibold" style={{ color: 'var(--mms-sidebar)' }}>{initials}</span>
                    <span className="flex-1 truncate text-sm text-mms-text">{name}</span>
                    <span className="hidden h-1.5 w-32 overflow-hidden rounded-full bg-mms-raised sm:block">
                      <span className="block h-full rounded-full bg-mms-accent" style={{ width: `${Math.round(((a.clicks || 0) / maxClicks) * 100)}%` }} />
                    </span>
                    <span className="w-12 text-right text-[12.5px] font-semibold mms-num text-mms-muted">{a.clicks}</span>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
